import {
  AnnotationCreationStage,
  AnnotationStage,
  ToolType,
} from '@readcloud/annotations';
import C from 'classnames';

import React, { useEffect } from 'react';
import { useCUDChangeManager } from '../../../hooks/useCUDChangeManager';

import {
  Annotation,
  CircleAnnotation,
  HtmlAnnotation,
  ImageAnnotation,
  KnownCloud,
  KnownUser,
  MediaAnnotation,
  OverlayAnnotation,
  PathAnnotation,
  RectangleAnnotation,
  TextHighlightAnnotation,
  UserPrefState,
} from '@readcloud/data';
import { bookSelectors, tagsSelectors } from '@readcloud/state';
import { useSelector } from 'react-redux';
import { TextSelectionContext } from '../../../context/TextSelection';

const getOverlayAnnoId = (obj: Annotation) => obj.id;

interface Props {
  canEditAnnotation: (annotation: Annotation) => boolean;
  canEditPublisherOptions: (id: string) => boolean;
  handleAdd: (
    annotation: Annotation & { version: '3'; authorID: string }
  ) => void | Promise<unknown>;
  handleUpdate: (annotation: Annotation) => void | Promise<unknown>;
  handleDelete: (id: string) => void | Promise<unknown>;
  handleToolChange: (tool: ToolType) => void;
  updateAnnoPreferences?: (
    type: string,
    data: Partial<Annotation['data']>
  ) => Promise<any>;
  annoPrefs?: UserPrefState['annoPrefs'];
  pageNumber: number;
  height: number;
  width: number;
  scale: number;
  knownUsers: KnownUser[];
  knownClouds: KnownCloud[];
  selectedAnnotation?: string;
  onSelectedAnnotationChange: (id?: string) => void;
  annotations: Annotation[];
  currentlyDrawing: boolean;
  currentTool: ToolType;
  userId: string;
  bookId: string;
  disableDrawing?: boolean;
  disableOverlay?: boolean;
  isPublisher: boolean;
  outerContainer?: HTMLDivElement;
  pagesContainer?: HTMLDivElement;
  pageContainer?: HTMLDivElement;
}

export const ShapesRenderer: React.FC<Props> = ({
  pageNumber,
  height,
  width,
  scale,
  knownUsers,
  knownClouds,
  selectedAnnotation,
  onSelectedAnnotationChange,
  annotations,
  currentlyDrawing,
  currentTool,
  userId,
  bookId,
  canEditAnnotation,
  canEditPublisherOptions,
  disableDrawing,
  disableOverlay,
  annoPrefs,
  outerContainer,
  pagesContainer,
  pageContainer,
  ...props
}) => {
  // This is the scale if the original resolution had been used to determine how much more the the pdf needed to be scaled to reach it's current size.
  //This is the value used by the old reader, and need to be consistent for rendering overlay annotations.
  const originalResolutionScale = React.useMemo(() => {
    return (scale || 1) * (96 / 72);
  }, [scale]);

  const books = useSelector(bookSelectors.getBooks);
  const tags = useSelector(tagsSelectors.getTags);
  const pageNumberRef = React.useRef(pageNumber);
  pageNumberRef.current = pageNumber;
  const { customTextSelection } = React.useContext(TextSelectionContext);
  const handleDelete = React.useCallback(
    (id: string) => {
      return props.handleDelete(id);
    },
    [pageNumber, props.handleDelete]
  );

  function isHtmlOrMedia(
    test: Annotation
  ): test is HtmlAnnotation | MediaAnnotation {
    return test.type === 'Media' || test.type === 'HTML';
  }

  function isNotHtmlOrMediaOrText(
    test: Annotation
  ): test is
    | ImageAnnotation
    | RectangleAnnotation
    | CircleAnnotation
    | OverlayAnnotation
    | PathAnnotation {
    return test.type != 'Media' && test.type != 'HTML' && test.type != 'Text';
  }

  function isHtml(test: Annotation): test is HtmlAnnotation {
    return test.type === 'HTML';
  }

  function isMedia(test: Annotation): test is MediaAnnotation {
    return test.type === 'Media';
  }

  function isText(test: Annotation): test is TextHighlightAnnotation {
    return test.type === 'Text';
  }

  const handleAddOverlay = React.useCallback(
    (obj: Annotation) => {
      let tempAnno: Annotation = obj;
      if (isHtml(tempAnno)) {
        tempAnno = {
          ...tempAnno,
          data: {
            ...tempAnno.data,
            rect: {
              ...tempAnno.data?.rect,
              height:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.height,
              width:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.width,
            },
          },
        };
        return props.handleAdd({
          ...tempAnno,
          version: '3',
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber },
          book: bookId,
          author: userId,
          authorID: userId,
          tags: [],
        });
      }
      if (isMedia(tempAnno)) {
        tempAnno = {
          ...tempAnno,
          data: {
            ...tempAnno.data,
            rect: {
              ...tempAnno.data?.rect,
              height:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.height,
              width:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.width,
            },
          },
        };
        return props.handleAdd({
          ...tempAnno,
          version: '3',
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber },
          book: bookId,
          author: userId,
          authorID: userId,
          tags: [],
        });
      }

      if (isText(tempAnno)) {
        return props.handleAdd({
          ...tempAnno,
          version: '3',
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber, range: '' },
          book: bookId,
          author: userId,
          authorID: userId,
          tags: [],
        });
      }

      if (isNotHtmlOrMediaOrText(tempAnno)) {
        return props.handleAdd({
          ...tempAnno,
          version: '3',
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber },
          book: bookId,
          author: userId,
          authorID: userId,
          tags: [],
        });
      }
    },

    [pageNumber, props.handleAdd]
  );

  const handleUpdateOverlay = React.useCallback(
    (obj: Annotation) => {
      let tempAnno: Annotation = obj;
      if (isHtml(tempAnno)) {
        tempAnno = {
          ...tempAnno,
          data: {
            ...tempAnno.data,
            rect: {
              ...tempAnno.data?.rect,
              height:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.height,
              width:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.width,
            },
          },
        };
        return props.handleUpdate({
          ...tempAnno,
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber },
          book: bookId,
          author: userId,
          tags: [],
        });
      }
      if (isMedia(tempAnno)) {
        tempAnno = {
          ...tempAnno,
          data: {
            ...tempAnno.data,
            rect: {
              ...tempAnno.data?.rect,
              height:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.height,
              width:
                tempAnno.data['variant'] === 'Icon'
                  ? 50
                  : tempAnno.data?.rect?.width,
            },
          },
        };
        return props.handleUpdate({
          ...tempAnno,
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber },
          book: bookId,
          author: userId,
          tags: [],
        });
      }

      if (isText(tempAnno)) {
        return props.handleUpdate({
          ...tempAnno,
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber, range: tempAnno.location?.range || '' },
          book: bookId,
          author: userId,
          tags: [],
        });
      }

      if (isNotHtmlOrMediaOrText(tempAnno)) {
        return props.handleUpdate({
          ...tempAnno,
          color: '#000000',
          bookType: 'PDF',
          selectedText: '',
          location: { pageNumber },
          book: bookId,
          author: userId,
          tags: [],
        });
      }
    },
    [pageNumber, props.handleUpdate]
  );

  const overlayChangeManager = useCUDChangeManager({
    getObjectId: getOverlayAnnoId,
    onCreate: handleAddOverlay,
    onUpdate: handleUpdateOverlay,
    onDelete: handleDelete,
    state: annotations,
  });

  const overlayAnnotationsState = {
    annotations: overlayChangeManager.localState,
  };

  const handleSelectAnno = React.useCallback(
    (id?: string) => {
      onSelectedAnnotationChange(id);
    },
    [onSelectedAnnotationChange]
  );

  const handleSavePreferences = (
    type: string,
    data: Partial<Annotation['data']>
  ) => {
    props.updateAnnoPreferences && props.updateAnnoPreferences(type, data);
  };

  React.useEffect(() => {
    if (currentlyDrawing) {
      // It may seem counterintuitive, but the saveChanges calls are in the "teardown" callback so that it gets called upon unmount as well as when currentlyDrawing goes from true -> false
      return () => {
        overlayChangeManager.saveChanges();
      };
    } else {
      return;
    }
  }, [currentlyDrawing]);

  const handleAddAndSave = React.useCallback(
    (anno: Annotation) => {
      overlayChangeManager.handleAdd(anno);
      overlayChangeManager.saveChanges();
      props.handleToolChange('Select');
    },
    [overlayChangeManager.handleAdd, props.handleToolChange]
  );

  const handleDeleteAndSave = React.useCallback(
    (id: string) => {
      overlayChangeManager.handleDelete(id);
      overlayChangeManager.saveChanges();
    },
    [overlayChangeManager.handleDelete]
  );

  const handleUpdateAndSave = React.useCallback(
    (obj: Annotation) => {
      overlayChangeManager.handleUpdate(obj);
      overlayChangeManager.saveChanges();
    },
    [overlayChangeManager.handleUpdate]
  );

  return (
    <>
      <div
        data-ignore-toolbar
        style={{
          top: 0,
          left: 0,
        }}
        className={C(
          !currentlyDrawing
            ? 'safariScrollFix' + customTextSelection
              ? 'iosTextSelectNone textSelectNone'
              : ''
            : 'editDisappearing',
          'rc-absolute',
          'isShapeLayer'
        )}
      >
        <AnnotationStage
          aboveContentZIndex={170}
          belowContentZIndex={125}
          canEditPublisherOptions={canEditPublisherOptions}
          canEditAnnotation={canEditAnnotation}
          containerHeight={height}
          containerWidth={width}
          editable={currentlyDrawing}
          scale={originalResolutionScale}
          onAnnotationChange={handleUpdateAndSave} // this needs to be handled completely client side, or you end up getting race conditions between local updates and updates from the server.
          onAnnotationDelete={handleDeleteAndSave}
          onAnnotationComment={() => {}}
          state={overlayAnnotationsState}
          knownUsers={knownUsers}
          knownClouds={knownClouds}
          selectedAnnotation={selectedAnnotation}
          onSelectedAnnotationChange={handleSelectAnno}
          mediaContent={{ mediaContent: books, tagsContent: tags }}
          onSavePreferences={handleSavePreferences}
          annoPrefs={annoPrefs}
          outerContainer={outerContainer}
          pagesContainer={pagesContainer}
          pageContainer={pageContainer}
          showPublisherOptions={props.isPublisher}
        />
      </div>

      {currentlyDrawing &&
      currentTool &&
      !disableOverlay &&
      currentTool !== 'Text' &&
      currentTool !== 'Select' ? (
        <div
          data-ignore-toolbar
          className={'rc-absolute'}
          style={{
            zIndex: 155 + 1,
            height: height,
            width: width,
            top: 0,
            left: 0,
          }}
        >
          <AnnotationCreationStage
            color="rgba(142, 209, 252, 0.6)"
            book={bookId}
            pageNumber={pageNumber}
            scale={originalResolutionScale}
            type={currentTool}
            author={userId}
            height={height}
            width={width}
            onCreateAnnotation={handleAddAndSave}
            showPublisherOptions={props.isPublisher}
            mediaContent={{ mediaContent: books, tagsContent: tags }}
            onSavePreferences={handleSavePreferences}
            annoPrefs={annoPrefs && annoPrefs}
          />
        </div>
      ) : null}
    </>
  );
};
